import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useApi, type ChannelCloseoutListItem } from '@/shared/api';
import { monthStart, shiftMonths, timelineWindow } from './channelCloseoutTimelineModel';

const OPEN_OPERATION_REFETCH_MS = 2_000;

/**
 * Solange eine Regel unterwegs ist, fragt die Ansicht alle zwei Sekunden nach, bis der Worker sie
 * bestätigt oder aufgibt; danach ist Ruhe. Hintergrund-Tabs fragen nicht (TanStack-Standard).
 */
export function refetchWhileUnconfirmed(items: readonly ChannelCloseoutListItem[] | undefined) {
  return items?.some((item) => item.syncStatus !== 'Synced' && item.syncStatus !== 'Failed')
    ? OPEN_OPERATION_REFETCH_MS
    : false;
}

/** Zwölf Monate ab dem laufenden Monat; zurück geht es höchstens bis dorthin. */
export function useCloseoutTimeline(propertyGroupId: string, today: string) {
  const api = useApi();
  const first = monthStart(today);
  const [anchor, setAnchor] = useState(first);
  const window = useMemo(() => timelineWindow(anchor), [anchor]);
  const query = useQuery({
    queryKey: [
      'availability-distribution',
      'closeouts',
      propertyGroupId,
      'timeline',
      window.from,
      window.to,
    ],
    queryFn: () =>
      api.availabilityDistribution.closeouts.list(propertyGroupId, 1, 'active', {
        from: window.from,
        to: window.to,
      }),
    placeholderData: (previous) => previous,
    refetchInterval: (current) => refetchWhileUnconfirmed(current.state.data?.items),
  });
  return {
    window,
    query,
    canShiftBack: anchor > first,
    shift: (months: number) =>
      setAnchor((current) => {
        const next = shiftMonths(current, months);
        return next < first ? first : next;
      }),
  };
}
